import type { NflTiebreakerTeam, TiebreakerStepFn } from '../../shared/tiebreaker/types';
import { RankMethod } from '../../shared/enums/rank-method';
import { createH2hPctStep } from '../../shared/tiebreaker/steps/h2h';
import { createRecordPctStep } from '../../shared/tiebreaker/steps/record-pct';
import { createCommonGamesStep } from '../../shared/tiebreaker/steps/common-games';
import { sovStep, sosStep } from '../../shared/tiebreaker/steps/sov-sos';
import { coinFlipStep } from '../../shared/tiebreaker/steps/coin-flip';
import { addExplanation, calcPctNaN, computeHthRecords } from '../../shared/tiebreaker/helpers';

/** Division-scoped H2H: only counts games between teams in the same division */
export const h2hDivStep = createH2hPctStep<NflTiebreakerTeam>(
  undefined,
  RankMethod.HTH,
  'DivHTH',
);

/** Conference-scoped H2H: all games between the tied teams */
export const h2hConfStep = createH2hPctStep<NflTiebreakerTeam>(
  undefined,
  RankMethod.HTH,
  'HTH',
);

export const h2hSweepStep: TiebreakerStepFn<NflTiebreakerTeam> = (teams, ctx) => {
  const records = computeHthRecords(teams, ctx.games);
  const twoTeamWinner = findTwoTeamH2hWinner(teams, records);

  if (twoTeamWinner !== null) {
    addExplanation(ctx, twoTeamWinner, 'HTH series.');
    return {
      winners: [twoTeamWinner],
      remaining: teams.filter(team => team !== twoTeamWinner),
      rankMethod: RankMethod.HTH,
    };
  }

  const winner = findStrictSweepWinner(teams, records);

  if (winner !== null) {
    addExplanation(ctx, winner, 'HTH sweep.');
    return {
      winners: [winner],
      remaining: teams.filter(team => team !== winner),
      rankMethod: RankMethod.HTH,
    };
  }

  const loser = findSweptLoser(teams, records);
  if (loser === null) {
    return { winners: [], remaining: teams };
  }

  const winners = teams.filter(team => team !== loser);
  for (const team of winners) {
    addExplanation(ctx, team, 'HTH swept loser eliminated.');
  }

  return {
    winners,
    remaining: [loser],
    rankMethod: RankMethod.HTH,
  };
};

function findTwoTeamH2hWinner(
  teams: NflTiebreakerTeam[],
  hthRecords: Map<string, Map<string, number[]>>,
): NflTiebreakerTeam | null {
  if (teams.length !== 2) {
    return null;
  }

  const [first, second] = teams;
  const record = hthRecords.get(first.teamAbbreviation)?.get(second.teamAbbreviation);
  if (record === undefined || record[0] + record[1] + record[2] === 0) {
    return null;
  }

  const pct = calcPctNaN(record);
  if (pct > 0.5) {
    return first;
  }
  if (pct < 0.5) {
    return second;
  }
  return null;
}

// Create NFL-specific typed versions of record steps
const nflDivPctStep = createRecordPctStep<NflTiebreakerTeam>(
  t => t.records.division.pctNaN, RankMethod.DivPCT, 'Div%',
);
const nflConfPctStep = createRecordPctStep<NflTiebreakerTeam>(
  t => t.records.conference.pctNaN, RankMethod.ConfPCT, 'Conf%',
);
const nflCommonGamesStep = createCommonGamesStep<NflTiebreakerTeam>(0);
const nflCommonGamesMin4Step = createCommonGamesStep<NflTiebreakerTeam>(4);
const noScoringDataStep: TiebreakerStepFn<NflTiebreakerTeam> = teams => ({
  winners: [],
  remaining: teams,
});

export const NFL_DIVISION_STEPS: TiebreakerStepFn<NflTiebreakerTeam>[] = [
  h2hDivStep,
  nflDivPctStep,
  nflCommonGamesMin4Step,
  nflConfPctStep,
  sovStep,
  sosStep,
  coinFlipStep as TiebreakerStepFn<NflTiebreakerTeam>,
];

export const NFL_CONFERENCE_STEPS: TiebreakerStepFn<NflTiebreakerTeam>[] = [
  h2hConfStep,
  nflConfPctStep,
  nflCommonGamesMin4Step,
  sovStep,
  sosStep,
  coinFlipStep as TiebreakerStepFn<NflTiebreakerTeam>,
];

const NFL_STEP_REGISTRY = new Map<string, TiebreakerStepFn<NflTiebreakerTeam>>([
  ['h2h-division', h2hDivStep],
  ['h2h-sweep', h2hSweepStep],
  ['h2h-scoring', noScoringDataStep],
  ['division-record', nflDivPctStep],
  ['conference-record', nflConfPctStep],
  ['common-games', nflCommonGamesStep],
  ['common-games-min-4', nflCommonGamesMin4Step],
  ['sov', sovStep],
  ['sos', sosStep],
  ['pf-pa-conf', noScoringDataStep],
  ['pf-pa-league', noScoringDataStep],
  ['net-points-conference', noScoringDataStep],
  ['net-points-common', noScoringDataStep],
  ['net-points-all', noScoringDataStep],
  ['net-touchdowns', noScoringDataStep],
  ['coin-flip', coinFlipStep as TiebreakerStepFn<NflTiebreakerTeam>],
]);

export function resolveNflSteps(
  stepKeys: string[] | null | undefined,
  fallback: TiebreakerStepFn<NflTiebreakerTeam>[],
): TiebreakerStepFn<NflTiebreakerTeam>[] {
  if (!stepKeys?.length) {
    return fallback;
  }

  return stepKeys.map(stepKey => {
    const step = NFL_STEP_REGISTRY.get(stepKey);
    if (!step) {
      throw new Error(`Unknown NFL tiebreaker step key: ${stepKey}`);
    }
    return step;
  });
}

function findSweptLoser(
  teams: NflTiebreakerTeam[],
  hthRecords: Map<string, Map<string, number[]>>,
): NflTiebreakerTeam | null {
  for (const candidate of teams) {
    const opponents = hthRecords.get(candidate.teamAbbreviation);
    if (!opponents) {
      continue;
    }

    const lostToAllOpponents = teams.every(other => {
      if (other === candidate) {
        return true;
      }

      const record = opponents.get(other.teamAbbreviation);
      return record !== undefined && record[0] === 0 && record[1] > 0 && record[2] === 0
        && calcPctNaN(record) < 0.5;
    });

    if (lostToAllOpponents) {
      return candidate;
    }
  }

  return null;
}

function findStrictSweepWinner(
  teams: NflTiebreakerTeam[],
  hthRecords: Map<string, Map<string, number[]>>,
): NflTiebreakerTeam | null {
  for (const candidate of teams) {
    const opponents = hthRecords.get(candidate.teamAbbreviation);
    if (!opponents) {
      continue;
    }

    const sweptAllOpponents = teams.every(other => {
      if (other === candidate) {
        return true;
      }

      const record = opponents.get(other.teamAbbreviation);
      return record !== undefined && record[0] > 0 && record[1] === 0 && record[2] === 0;
    });

    if (sweptAllOpponents) {
      return candidate;
    }
  }

  return null;
}
